import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { client } from "../lib/sanity";
import { urlForImage } from "../lib/sanityImageUrl";

const RecommendBoxPostPopular = ({ currentSlug = "" }) => {
  const { t } = useTranslation(['common', 'blog']);
  const [row1Posts, setRow1Posts] = useState([]);
  const [row2Posts, setRow2Posts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Improved navigation handler for better mobile support
  const handleNavigation = (path) => (e) => {
    e.preventDefault();

    // Store a flag in session storage to indicate we should scroll to top
    sessionStorage.setItem("scrollToTop", "true");

    // Navigate to the new page
    navigate(path);
  };

  // Effect to handle scrolling when the component mounts or location changes
  useEffect(() => {
    const shouldScrollToTop = sessionStorage.getItem("scrollToTop") === "true";

    if (shouldScrollToTop) {
      // Clear the flag
      sessionStorage.removeItem("scrollToTop");

      // Multiple scroll attempts for better reliability across devices
      window.scrollTo(0, 0);

      // Additional attempts with timeouts for mobile devices
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      }, 200);

      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 500);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Fetch the 6 most recent posts, excluding the current post by slug
        // Filtering by slug.current excludes both published and draft versions
        const query = `*[_type == "post" && slug.current != $currentSlug && defined(mainImage.asset._ref)] | order(publishedAt desc)[0...6] {
          _id,
          title,
          slug,
          mainImage{
            asset->{
              url,
              metadata{
                dimensions
              }
            },
            caption,
            alt
          },
          publishedAt,
          categories[]->{
            title,
            slug
          },
          claps,
          readTime,
          excerpt
        }`;

        const posts = await client.fetch(query, {
          currentSlug: currentSlug || "__no_match__",
        });

        console.log("Latest posts found:", posts.length);

        // Helper to fill with empty placeholder cards if needed
        const fillWithEmptyCards = (posts, targetCount) => {
          const filled = [...posts];
          while (filled.length < targetCount) {
            filled.push(createEmptyCard(filled.length));
          }
          return filled.slice(0, targetCount);
        };

        setRow1Posts(fillWithEmptyCards(posts.slice(0, 3), 3));
        setRow2Posts(fillWithEmptyCards(posts.slice(3, 6), 3));
      } catch (error) {
        console.error("Error fetching posts:", error);
        setError(error.message);
        setRow1Posts(createEmptyCards(3));
        setRow2Posts(createEmptyCards(3));
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [currentSlug]);

  // Helper functions for empty cards
  const createEmptyCard = (index) => ({
    _id: `empty-${index}`,
    title: "",
    slug: { current: "" },
    isEmpty: true,
  });

  const createEmptyCards = (count) =>
    Array(count)
      .fill(null)
      .map((_, i) => createEmptyCard(i));

  // Render post card component
  const renderPostCard = (post, index) => (
    <div key={post._id} className='col-md-4 mb-4 xs:px-0'>
      {!post.isEmpty ? (
        <div className='bg-white rounded-sm overflow-hidden h-full flex flex-col'>
          <a
            className='flex aspect-[16/9] overflow-hidden'
            href={`/blog/${post.slug.current}`}
            onClick={handleNavigation(`/blog/${post.slug.current}`)}
          >
            {post.mainImage?.asset ? (
              <img
                src={urlForImage(post.mainImage).url()}
                alt={post.title}
                className='w-full h-[220px] object-cover transition-transform duration-300 hover:scale-105 hover:rounded-xl xs:px-8 rounded-xl'
                onError={(e) => {
                  const imgElement = e.target as HTMLImageElement;
                  imgElement.onerror = null;
                  imgElement.src = "/assets/img/fallback-image.jpg";
                }}
              />
            ) : (
              <div className='w-full h-[220px] bg-gray-200'></div>
            )}
          </a>
          <div className='p-6 flex flex-col flex-grow'>
            <a
              className='block text-[17px] tracking-tight leading-[1.4] font-[Oxanium] font-normal text-inaqpurple mb-2 hover:text-inaqgold normal-case'
              href={`/blog/${post.slug.current}`}
              onClick={handleNavigation(`/blog/${post.slug.current}`)}
            >
              {post.title}
            </a>
            {post.categories && post.categories.length > 0 && (
              <div className='mb-2'>
                <span className='text-xs text-inaqgold font-rubik'>
                  {post.categories[0].title}
                </span>
              </div>
            )}
            {post.readTime && (
              <div className='text-xs text-gray-500 font-rubik'>
                {post.readTime} {t('common:blog.readTime', 'min čítania')}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className='bg-white rounded-sm overflow-hidden h-full flex flex-col opacity-30'>
          <div className='w-full h-[220px] bg-gray-200'></div>
          <div className='p-6 flex flex-col flex-grow'>
            <div className='h-6 bg-gray-200 rounded w-3/4 mb-2'></div>
            <div className='h-4 bg-gray-200 rounded w-1/2'></div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return null;
  }

  return (
    <div className='container mt-24 sm:mt-16 mb-24'>
      {/* Main Header */}
      <div className='justify-center row'>
        <div className='col-12 col-md-10 text-center mb-16 sm:mb-12'>
          <h2 className='inaq-subtitle inaq-subtitle-blogoverlay heading-5 font-rubik text-[52px] sm:!text-[30px] text-black font-semibold tracking-[-2px] lg:leading-[52px] cursor-default'>
            {t('blog:recommend.heading', 'Prečítajte si viac z nášho blogu')}
            {error && (
              <div style={{ color: "red", fontSize: "16px" }}>
                (Error: {error})
              </div>
            )}
          </h2>
          <span className='inaq-subtitle-blogoverlay-sub font-rubik text-[18px] lg:text-[16px] lg:leading-[28px] text-gray-500 inline-block mx-8 lg:mx-0 font-light'>
            {t(
              'blog:recommend.subtext',
              'Pomáhame firmám v rôznych oblastiach marketingu a automatizácie. Naše postrehy, vedomosti a skúsenosti niekedy zdieľame v long-form článkoch.'
            )}
          </span>
        </div>
      </div>

      {/* Row 1 — 3 latest posts */}
      <div className='grid-wrapper sm:mx-10 mb-4'>
        <div className='row'>
          {row1Posts.map((post, index) => renderPostCard(post, index))}
        </div>
      </div>

      {/* Row 2 — next 3 latest posts */}
      <div className='grid-wrapper sm:mx-10'>
        <div className='row'>
          {row2Posts.map((post, index) => renderPostCard(post, index))}
        </div>
      </div>

      <div className='flex justify-center w-full mt-8'>
        <a
          href='/blog'
          className='cursor-pointer flex justify-center'
          onClick={handleNavigation("/blog")}
        >
          <button className='group relative inline-flex items-center justify-center tracking-[1px] font-[oxanium] text-sm md:mb-[15px] sm:mt-0 text-white border-[1px] bg-inaqpurple bg-opacity-80 hover:bg-opacity-100 border-inaqpurple rounded-[2px] font-medium transition-all duration-300 ease-in-out'>
            <span className='relative z-10 px-4 py-2'>
              {t('blog:recommend.viewAll', 'Prejdite na všetky články')}
            </span>
          </button>
        </a>
      </div>
    </div>
  );
};

export default RecommendBoxPostPopular;
